import { useMemo } from "react";
import {
    Area, AreaChart, CartesianGrid, Cell, Legend, Pie, PieChart,
    ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { Activity, PieChart as PieIcon, UserCheck, Users } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Traffic chart — at-a-glance daily visits & unique visitors.
 * Renders only when `traffic` data is provided. Defensive against missing
 * fields so it never crashes the dashboard.
 */
export default function TrafficChart({ traffic, sources }) {
    const { t, i18n } = useTranslation();
    const series = traffic?.series || [];
    const totals = traffic?.totals || {};

    const data = useMemo(
        () => series.map((row) => ({
            day: row.day,
            label: formatDayShort(row.day, i18n.language),
            visits: row.visits ?? 0,
            unique: row.unique_visitors ?? 0,
        })),
        [series, i18n.language],
    );

    return (
        <section
            data-testid="admin-traffic-chart"
            className="border border-[var(--border)] bg-[var(--bg-elevated)] rounded-md p-6 mb-12"
        >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
                <h2
                    className="font-display text-2xl text-[var(--ink)] inline-flex items-center gap-3"
                    style={{ fontVariationSettings: '"wght" 600, "wdth" 88, "opsz" 48' }}
                >
                    <Activity size={18} strokeWidth={1.5} className="text-[var(--accent)]" />
                    {t("admin_traffic.title")}
                </h2>
                <p className="text-xs text-[var(--ink-dim)]">
                    {t("admin_traffic.window", { n: traffic?.days ?? series.length })}
                </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-6">
                <Stat
                    icon={Activity}
                    label={t("admin_traffic.visits")}
                    value={totals.visits ?? 0}
                    testId="traffic-stat-visits"
                />
                <Stat
                    icon={UserCheck}
                    label={t("admin_traffic.unique_visitors")}
                    value={totals.unique_visitors_period ?? 0}
                    testId="traffic-stat-uniques"
                />
                <Stat
                    icon={Users}
                    label={t("admin_traffic.registered_users")}
                    value={totals.registered_users_total ?? 0}
                    testId="traffic-stat-registered"
                />
            </div>

            <div className="h-64 w-full" data-testid="admin-traffic-chart-canvas" style={{ minHeight: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data} margin={{ top: 4, right: 12, left: -12, bottom: 0 }}>
                        <defs>
                            <linearGradient id="visitsGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="var(--accent)" stopOpacity={0.55} />
                                <stop offset="100%" stopColor="var(--accent)" stopOpacity={0.02} />
                            </linearGradient>
                            <linearGradient id="uniquesGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="var(--ink)" stopOpacity={0.35} />
                                <stop offset="100%" stopColor="var(--ink)" stopOpacity={0.02} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                        <XAxis
                            dataKey="label"
                            stroke="var(--ink-muted)"
                            tick={{ fontSize: 11 }}
                            tickLine={false}
                            axisLine={{ stroke: "var(--border)" }}
                            interval="preserveStartEnd"
                            minTickGap={24}
                        />
                        <YAxis
                            stroke="var(--ink-muted)"
                            tick={{ fontSize: 11 }}
                            tickLine={false}
                            axisLine={{ stroke: "var(--border)" }}
                            allowDecimals={false}
                            width={36}
                        />
                        <Tooltip content={<ChartTooltip t={t} />} />
                        <Area
                            type="monotone"
                            dataKey="visits"
                            name={t("admin_traffic.visits")}
                            stroke="var(--accent)"
                            strokeWidth={2}
                            fill="url(#visitsGrad)"
                        />
                        <Area
                            type="monotone"
                            dataKey="unique"
                            name={t("admin_traffic.unique_visitors")}
                            stroke="var(--ink)"
                            strokeWidth={1.5}
                            fill="url(#uniquesGrad)"
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>

            {sources && (sources.sources?.length > 0) && (
                <TrafficSources sources={sources} t={t} />
            )}
        </section>
    );
}

const SOURCE_COLORS = {
    direct: "#6b6558",
    search: "#9c5441",
    social: "#b89461",
    email: "#3d5a6c",
    referral: "#c8a98a",
    other: "#443f33",
};

function TrafficSources({ sources, t }) {
    const pie = (sources.sources || []).filter((s) => s.visits > 0);
    const topHosts = (sources.top_hosts || []).slice(0, 6);

    return (
        <div
            data-testid="admin-traffic-sources"
            className="mt-8 pt-6 border-t border-[var(--border)] grid grid-cols-1 md:grid-cols-2 gap-8"
        >
            <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] mb-3 inline-flex items-center gap-1.5">
                    <PieIcon size={11} strokeWidth={1.5} />
                    {t("admin_traffic.sources_title")}
                </p>
                {pie.length === 0 ? (
                    <p className="text-xs text-[var(--ink-dim)] italic">—</p>
                ) : (
                    <div className="h-56 w-full" style={{ minHeight: 220 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={pie.map((s) => ({
                                        name: t(`admin_traffic.source.${s.bucket}`, { defaultValue: s.bucket }),
                                        value: s.visits,
                                        bucket: s.bucket,
                                        share: s.share_percent,
                                    }))}
                                    dataKey="value"
                                    nameKey="name"
                                    innerRadius={44}
                                    outerRadius={80}
                                    stroke="var(--bg-elevated)"
                                    strokeWidth={2}
                                >
                                    {pie.map((s) => (
                                        <Cell key={s.bucket} fill={SOURCE_COLORS[s.bucket] || SOURCE_COLORS.other} />
                                    ))}
                                </Pie>
                                <Tooltip content={<SourceTooltip />} />
                                <Legend
                                    wrapperStyle={{ fontSize: 11, color: "var(--ink-muted)" }}
                                    iconType="circle"
                                    formatter={(v) => <span className="text-[var(--ink-muted)]">{v}</span>}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
            <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] mb-3">
                    {t("admin_traffic.top_hosts")}
                </p>
                {topHosts.length === 0 ? (
                    <p className="text-xs text-[var(--ink-dim)] italic">
                        {t("admin_traffic.top_hosts_empty")}
                    </p>
                ) : (
                    <ul className="space-y-2" data-testid="admin-traffic-top-hosts">
                        {topHosts.map((h) => (
                            <li
                                key={h.host}
                                className="flex items-center justify-between text-sm text-[var(--ink-muted)] border-b border-[var(--border)] pb-1.5"
                            >
                                <span className="truncate mr-3">{h.host}</span>
                                <span className="text-[var(--ink)] font-mono-num">{h.visits}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function SourceTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const p = payload[0];
    return (
        <div className="bg-[var(--bg)] border border-[var(--border-strong)] rounded-md px-3 py-2 text-xs">
            <p className="text-[var(--ink)] flex items-center gap-2">
                <span
                    className="inline-block w-2 h-2 rounded-full"
                    style={{ background: p.payload?.fill || p.color }}
                />
                <span className="text-[var(--ink-muted)]">{p.name}:</span>
                <span className="font-mono-num">{p.value}</span>
                {typeof p.payload?.share === "number" && (
                    <span className="text-[var(--ink-dim)]">({p.payload.share}%)</span>
                )}
            </p>
        </div>
    );
}

function Stat({ icon: Icon, label, value, testId }) {
    return (
        <div data-testid={testId}>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] mb-2 inline-flex items-center gap-1.5">
                <Icon size={11} strokeWidth={1.5} />
                {label}
            </p>
            <p
                className="font-display text-3xl text-[var(--ink)]"
                style={{ fontVariationSettings: '"wght" 600, "wdth" 84, "opsz" 48' }}
            >
                {Number(value).toLocaleString()}
            </p>
        </div>
    );
}

function ChartTooltip({ active, payload, label, t }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-[var(--bg)] border border-[var(--border-strong)] rounded-md px-3 py-2 text-xs">
            <p className="text-[var(--ink-muted)] uppercase tracking-widest text-[10px] mb-1">{label}</p>
            {payload.map((p) => (
                <p key={p.dataKey} className="text-[var(--ink)] flex items-center gap-2">
                    <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{ background: p.color }}
                    />
                    <span className="text-[var(--ink-muted)]">{p.name}:</span>
                    <span className="font-mono-num">{p.value}</span>
                </p>
            ))}
        </div>
    );
}

function formatDayShort(iso, lang) {
    if (!iso) return "";
    try {
        const d = new Date(iso + "T00:00:00Z");
        return d.toLocaleDateString(lang || "en", { month: "short", day: "numeric" });
    } catch {
        return iso;
    }
}
